import { useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { Coordinates, PropertyRecord } from '@growth-rings/fmos-schema';
import 'maplibre-gl/dist/maplibre-gl.css';

interface PropertyMapProps {
  property?: PropertyRecord;
  compact?: boolean;
  onCoordinatesResolved?: (coordinates: Coordinates) => void | Promise<void>;
}

const mapTilerKey = (import.meta.env.VITE_MAPTILER_KEY as string | undefined)?.trim();
export const addressSearchAvailable = Boolean(mapTilerKey);
const openStreetMapStyle = () => ({
  version: 8 as const,
  sources: {
    openStreetMap: {
      type: 'raster' as const,
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors'
    }
  },
  layers: [{ id: 'openStreetMap', type: 'raster' as const, source: 'openStreetMap' }]
});
export interface AddressSuggestion {
  id: string; label: string; address: string; municipality: string; region: string; coordinates: Coordinates;
}
export async function searchAddresses(query: string, signal?: AbortSignal): Promise<AddressSuggestion[]> {
  if (!mapTilerKey || query.trim().length < 3) return [];
  const url = new URL(`https://api.maptiler.com/geocoding/${encodeURIComponent(query.trim())}.json`);
  url.searchParams.set('key', mapTilerKey); url.searchParams.set('country', 'us');
  url.searchParams.set('limit', '5'); url.searchParams.set('autocomplete', 'true');
  url.searchParams.set('types', 'address');
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('Address suggestions are unavailable. You can enter the address and municipality manually.');
  const payload = await response.json() as { features?: Array<{ id: string; place_name?: string; text?: string; address?: string; center?: number[]; context?: Array<{ id: string; text: string }> }> };
  return (payload.features ?? []).filter(f => f.center?.length === 2 && f.center.every(Number.isFinite)).map(f => ({
    id: f.id, label: f.place_name ?? f.text ?? '', address: [f.address, f.text].filter(Boolean).join(' '),
    municipality: f.context?.find(c => /^(municipality|place)\./.test(c.id))?.text ?? '',
    region: f.context?.find(c => /^region\./.test(c.id))?.text ?? '',
    coordinates: { longitude: f.center![0], latitude: f.center![1] }
  }));
}

export async function geocodeWithMapTiler(query: string): Promise<Coordinates> {
  if (!mapTilerKey) throw new Error('Add VITE_MAPTILER_KEY before locating addresses with MapTiler.');
  const url = new URL(`https://api.maptiler.com/geocoding/${encodeURIComponent(query)}.json`);
  url.searchParams.set('key', mapTilerKey);
  url.searchParams.set('country', 'us');
  url.searchParams.set('limit', '1');
  const response = await fetch(url);
  if (!response.ok) throw new Error(`MapTiler address lookup returned HTTP ${response.status}. ${[401, 403].includes(response.status) ? 'Check your browser key and allowed HTTP origins.' : 'Check connection, usage allowance, and address.'}`);
  const payload = await response.json() as { features?: Array<{ center?: [number, number] }> };
  const center = payload.features?.[0]?.center;
  if (!center) throw new Error('No confident map location was found. Add a GPS point instead.');
  return { longitude: center[0], latitude: center[1] };
}

export function PropertyMap({ property, compact = false, onCoordinatesResolved }: PropertyMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [coordinates, setCoordinates] = useState<Coordinates | undefined>(property?.coordinates);
  const [message, setMessage] = useState('');
  const [mapError, setMapError] = useState('');

  useEffect(() => { setCoordinates(property?.coordinates); setMessage(''); }, [property?.id, property?.coordinates?.latitude, property?.coordinates?.longitude]);

  useEffect(() => {
    let active = true;
    if (!property || coordinates || !mapTilerKey || !property.address) return;
    setMessage('Locating this property from its saved address…');
    void geocodeWithMapTiler(`${property.address}, ${property.municipality}, ${property.region}`).then(async (result) => {
      if (!active) return;
      setCoordinates(result);
      setMessage('Map location added from the saved address. Confirm before relying on it.');
      await onCoordinatesResolved?.(result);
    }).catch((error) => { if (active) setMessage(error instanceof Error ? error.message : 'The property could not be located.'); });
    return () => { active = false; };
  }, [property?.id, property?.address, property?.municipality, property?.region, coordinates]);

  useEffect(() => {
    if (!containerRef.current || !coordinates) return;
    let active = true;
    setMapError('');
    const timeout = window.setTimeout(() => {
      if (!active) return;
      if (mapTilerKey && mapRef.current) {
        mapRef.current.setStyle(openStreetMapStyle());
        setMapError('MapTiler did not respond; showing the OpenStreetMap fallback.');
      } else {
        setMapError('Map tiles are taking too long to load. Check your connection.');
      }
    }, 12000);
    void import('maplibre-gl').then(({ Map: LiveMap, Marker, NavigationControl }) => {
      if (!active || !containerRef.current) return;
      const map = new LiveMap({
        container: containerRef.current,
        style: mapTilerKey
          ? `https://api.maptiler.com/maps/outdoor-v2/style.json?key=${encodeURIComponent(mapTilerKey)}`
          : openStreetMapStyle(),
        center: [coordinates.longitude, coordinates.latitude],
        zoom: compact ? 14 : 15,
        attributionControl: {}
      });
      map.addControl(new NavigationControl({ showCompass: true }), 'top-right');
      map.on('load', () => { if (active) { window.clearTimeout(timeout); setMapError(''); } });
      map.on('error', () => { if (active) setMapError('Some map resources could not load. Check the map configuration and connection. The marker alone does not confirm that the base map loaded.'); });
      const markerElement = document.createElement('div');
      markerElement.className = 'heliodome-marker';
      markerElement.setAttribute('aria-label', `${property?.name ?? 'Property'} location`);
      new Marker({ element: markerElement }).setLngLat([coordinates.longitude, coordinates.latitude]).addTo(map);
      mapRef.current = map;
    }).catch(() => { if (active) setMapError('The live map could not be loaded. Refresh to try again.'); });
    return () => { active = false; window.clearTimeout(timeout); mapRef.current?.remove(); mapRef.current = null; };
  }, [property?.id, coordinates?.latitude, coordinates?.longitude, compact]);

  if (!property) return <div className={`property-map-live map-placeholder ${compact ? 'compact' : ''}`}><strong>Add a property to open its map.</strong></div>;
  if (!coordinates) return <div className={`property-map-live map-placeholder ${compact ? 'compact' : ''}`}><strong>{message.includes('HTTP') ? 'Map temporarily unavailable' : 'Locate this property'}</strong><small>{message || 'Add a saved address or GPS point to open the map.'}</small></div>;

  return <div className={`property-map-live ${compact ? 'compact' : ''}`}>
    <div ref={containerRef} className="maptiler-canvas" />
    {mapError && <div className="map-load-error" role="alert">{mapError}</div>}
    <div className="heliodome-map-label"><span className="pill orange">{mapTilerKey ? 'Helio Dome · MapTiler' : 'Property base map'}</span><strong>{property.name}</strong><small>{message || (mapTilerKey ? 'Property location · owner-controlled record' : 'Saved property location · OpenStreetMap base layer')}</small></div>
  </div>;
}
